/**
 * TaxCalendar — Central Tax Calendar (firm-wide view of every tax deadline).
 *
 * PRD: unified place to see GST + Annual + Provisional deadlines across all
 * clients, filterable by tax type / urgency / status / assignee / client.
 * Reuses the existing Job data model — no new tables. Sorted by deadline
 * ascending with overdue floated to the top.
 *
 * Row actions:
 *   • Fire a cadence email now (staff-triggered, respects double-send guard)
 *   • Jump into Firm Jobs pre-filtered to that client
 *   • Open the client-facing review page (deep-link)
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCcw, Loader2, CalendarClock, AlertTriangle, Flame, Clock, CheckCircle2, Zap, Mail, ExternalLink, X } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const KIND_TABS = [
    { key: "all", label: "All tax" },
    { key: "gst", label: "GST" },
    { key: "annual", label: "Annual" },
    { key: "provisional", label: "Provisional" },
];

const URGENCY_TABS = [
    { key: "all", label: "All" },
    { key: "overdue", label: "Overdue", tone: "red" },
    { key: "due_soon", label: "Due ≤ 7 days", tone: "amber" },
    { key: "due_month", label: "Due this month", tone: "sky" },
    { key: "upcoming", label: "Upcoming", tone: "slate" },
];

const STATUSES = [
    { key: "all", label: "Any status" },
    { key: "data_received", label: "Data received" },
    { key: "wip", label: "In progress" },
    { key: "awaiting_client", label: "Awaiting client" },
    { key: "review", label: "In review" },
    { key: "filed", label: "Filed" },
];

const STATUS_LABELS = {
    data_received: "Data received",
    wip: "In progress",
    review: "In review",
    awaiting_client: "Awaiting client",
    filed: "Filed",
    completed: "Completed",
};

const REVIEW_LABELS = {
    confirmed: "Client confirmed",
    change_requested: "Change requested",
};

function urgencyTone(u) {
    if (u === "overdue") return "bg-red-100 text-red-800 border-red-200";
    if (u === "due_soon") return "bg-amber-100 text-amber-900 border-amber-200";
    if (u === "due_month") return "bg-sky-50 text-sky-800 border-sky-200";
    if (u === "upcoming") return "bg-slate-50 text-slate-700 border-slate-200";
    return "bg-zinc-50 text-zinc-600 border-zinc-200";
}

function daysOutLabel(d) {
    if (d === null || d === undefined) return "No deadline";
    if (d < 0) return `${Math.abs(d)}d overdue`;
    if (d === 0) return "Due today";
    if (d === 1) return "Due tomorrow";
    return `${d}d left`;
}

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
}

export default function TaxCalendar() {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [stages, setStages] = useState([14, 10, 7, 5, 2]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [taxType, setTaxType] = useState("all");
    const [urgency, setUrgency] = useState("all");
    const [status, setStatus] = useState("all");
    const [q, setQ] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (taxType && taxType !== "all") params.set("tax_type", taxType);
            if (urgency && urgency !== "all") params.set("urgency", urgency);
            if (status && status !== "all") params.set("status", status);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/tax/calendar?${params.toString()}`);
            setRows(data.rows || []);
            setSummary(data.summary || {});
            setStages(data.stages || [14, 10, 7, 5, 2]);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [taxType, urgency, status, q]);

    useEffect(() => { load(); }, [load]);

    async function fireCadence(row) {
        // Pick the next stage to send. For jobs still ahead of a milestone,
        // fire the closest upcoming one; for overdue jobs pick the smallest
        // (most urgent) unfired stage. Respects the double-send guard.
        const fired = new Set(row.stages_fired || []);
        const remaining = stages.filter((s) => !fired.has(s));
        if (remaining.length === 0) { toast.error("All cadence stages already fired for this job."); return; }
        let stage;
        if (row.days_out === null || row.days_out === undefined || row.days_out < 0) {
            stage = Math.min(...remaining);
        } else {
            const match = remaining.find((s) => s >= row.days_out);
            stage = match || Math.min(...remaining);
        }
        setBusyId(row.id);
        try {
            await api.post(`/tax/${row.id}/fire-cadence`, { stage });
            toast.success(`Sent stage ${stage}-day reminder`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusyId(null);
        }
    }

    const kpi = useMemo(() => ([
        { key: "overdue", label: "Overdue", value: summary.overdue || 0, icon: Flame, tone: "text-red-700 bg-red-50 border-red-200" },
        { key: "due_soon", label: "Due ≤ 7 days", value: summary.due_soon || 0, icon: AlertTriangle, tone: "text-amber-800 bg-amber-50 border-amber-200" },
        { key: "due_month", label: "This month", value: summary.due_month || 0, icon: CalendarClock, tone: "text-sky-800 bg-sky-50 border-sky-200" },
        { key: "awaiting_client_review", label: "Change requested", value: summary.awaiting_client_review || 0, icon: Clock, tone: "text-orange-800 bg-orange-50 border-orange-200" },
        { key: "confirmed_awaiting_filing", label: "Client confirmed", value: summary.confirmed_awaiting_filing || 0, icon: CheckCircle2, tone: "text-emerald-800 bg-emerald-50 border-emerald-200" },
    ]), [summary]);

    return (
        <div className="space-y-4">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">Tax calendar</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">Every tax deadline for the firm — sorted by what needs attention first.</p>
                </div>
                <Button variant="outline" onClick={load} disabled={loading} data-testid="tax-cal-refresh">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</>}
                </Button>
            </header>

            {/* KPI tiles — clickable to filter by urgency */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {kpi.map((k) => {
                    const Icon = k.icon;
                    const clickable = ["overdue", "due_soon", "due_month"].includes(k.key);
                    const active = urgency === k.key;
                    return (
                        <button
                            key={k.key}
                            type="button"
                            onClick={() => clickable && setUrgency(active ? "all" : k.key)}
                            className={`text-left rounded-xl border p-3 transition ${k.tone} ${active ? "ring-2 ring-offset-1 ring-[#0F1115]" : ""} ${clickable ? "hover:shadow-sm cursor-pointer" : "cursor-default"}`}
                            data-testid={`tax-cal-kpi-${k.key}`}
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] uppercase tracking-wider opacity-80">{k.label}</span>
                                <Icon className="h-4 w-4 opacity-70" />
                            </div>
                            <div className="mt-1 text-2xl font-semibold tabular-nums">{k.value}</div>
                        </button>
                    );
                })}
            </div>

            {/* Filter bar */}
            <Card className="p-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <div className="flex gap-1" data-testid="tax-cal-kind-tabs">
                        {KIND_TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setTaxType(t.key)}
                                className={`h-8 rounded-full border px-3 text-[12.5px] ${taxType === t.key ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}
                                data-testid={`tax-cal-kind-${t.key}`}
                            >
                                {t.label}{summary.by_kind && t.key !== "all" ? ` · ${summary.by_kind[`${t.key === "gst" ? "gst_return" : t.key === "annual" ? "annual_return" : "provisional_tax"}`] || 0}` : ""}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1 ml-auto" data-testid="tax-cal-urgency-tabs">
                        {URGENCY_TABS.map((t) => (
                            <button
                                key={t.key}
                                onClick={() => setUrgency(t.key)}
                                className={`h-8 rounded-full border px-3 text-[12.5px] ${urgency === t.key ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}
                                data-testid={`tax-cal-urgency-${t.key}`}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Input
                        placeholder="Search client…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="max-w-xs h-9"
                        data-testid="tax-cal-search"
                    />
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="h-9 rounded-md border border-[#E4E4E7] bg-white text-[13px] px-2"
                        data-testid="tax-cal-status"
                    >
                        {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                    </select>
                    {(taxType !== "all" || urgency !== "all" || status !== "all" || q) && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setTaxType("all"); setUrgency("all"); setStatus("all"); setQ(""); }}
                            data-testid="tax-cal-clear-filters"
                            className="text-[12px]"
                        >
                            <X className="h-3 w-3 mr-1" /> Clear
                        </Button>
                    )}
                </div>
            </Card>

            {/* Rows */}
            <Card className="p-0 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]">
                        <Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading tax calendar…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]" data-testid="tax-cal-empty">
                        Nothing matches these filters. Try clearing them or importing GST/Annual jobs.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]" data-testid="tax-cal-table">
                            <thead className="bg-[#FAFAF7] text-[11.5px] uppercase tracking-wider text-[#5E636E]">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Deadline</th>
                                    <th className="text-left px-3 py-2 font-medium">Client</th>
                                    <th className="text-left px-3 py-2 font-medium">Kind</th>
                                    <th className="text-left px-3 py-2 font-medium">Period</th>
                                    <th className="text-left px-3 py-2 font-medium">Our target</th>
                                    <th className="text-left px-3 py-2 font-medium">Cadence</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-right px-3 py-2 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r) => (
                                    <tr key={r.id} className="border-t border-[#EEEDE7] hover:bg-[#FAFAF7]" data-testid={`tax-cal-row-${r.id}`}>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="flex flex-col">
                                                <span className={`inline-flex w-fit items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${urgencyTone(r.urgency)}`}>
                                                    {daysOutLabel(r.days_out)}
                                                </span>
                                                <span className="text-[11.5px] text-[#5E636E] mt-1">{fmt(r.filing_deadline)}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <Link
                                                to={`/app/clients/${r.client_id}`}
                                                className="text-[#0F1115] hover:underline font-medium"
                                                data-testid={`tax-cal-client-${r.id}`}
                                            >
                                                {r.client_name || "—"}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[#5E636E]">{r.kind_label}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[#5E636E]">{r.period_label || "—"}</td>
                                        <td className="px-3 py-2 whitespace-nowrap text-[#5E636E]">{fmt(r.cops_target_date)}</td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1" title={`Stages fired: ${r.stages_fired.join(", ") || "none"}`}>
                                                {stages.map((s) => (
                                                    <span
                                                        key={s}
                                                        className={`h-1.5 w-4 rounded-sm ${r.stages_fired.includes(s) ? "bg-emerald-500" : "bg-zinc-200"}`}
                                                    />
                                                ))}
                                                <span className="ml-1 text-[11px] text-[#5E636E] tabular-nums">{r.cadence_count}/{stages.length}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex flex-col gap-0.5">
                                                <span className="text-[12px] text-[#0F1115]">{STATUS_LABELS[r.status] || r.status || "—"}</span>
                                                {r.client_review_status && (
                                                    <span className={`text-[10.5px] ${r.client_review_status === "confirmed" ? "text-emerald-700" : "text-orange-700"}`}>
                                                        {REVIEW_LABELS[r.client_review_status] || r.client_review_status}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-right">
                                            <div className="inline-flex gap-1">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => fireCadence(r)}
                                                    disabled={busyId === r.id || (r.stages_fired || []).length >= stages.length}
                                                    className="h-7 text-[11.5px]"
                                                    data-testid={`tax-cal-fire-${r.id}`}
                                                    title="Send next cadence email now"
                                                >
                                                    {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Zap className="h-3 w-3 mr-1" /> Nudge</>}
                                                </Button>
                                                <Link to={`/portal/tax/review/${r.id}`} target="_blank" rel="noopener noreferrer">
                                                    <Button variant="ghost" size="sm" className="h-7 text-[11.5px]" data-testid={`tax-cal-open-${r.id}`} title="Open client review page">
                                                        <ExternalLink className="h-3 w-3" />
                                                    </Button>
                                                </Link>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {!loading && rows.length > 0 && (
                    <div className="border-t border-[#EEEDE7] bg-[#FAFAF7] px-3 py-2 text-[11.5px] text-[#5E636E]">
                        Showing {rows.length} tax job{rows.length === 1 ? "" : "s"} · Nudge = send the next unfired cadence stage now (respects the auto-scheduler's double-send guard).
                    </div>
                )}
            </Card>
        </div>
    );
}
